import React, { FC, useEffect, useState } from 'react';
import ActionButton from 'components/ActionButton/Index';
import Popup from '../Popups/Popup/Index';
import TalentOverviewPopup from '../Popups/TalentOverview/Index';
import Button from 'components/Button/Index';
import AssessmentPopup from '../Popups/Assessment/Index';
import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { useSelector } from 'react-redux';
import { IMe } from 'core/interface/me';
import { RootState } from 'redux/store';
import { getDateFormatter } from 'helpers/dateConverter';
import RadarChartComp from '../RadarChart/RadarChartComp';
import {
  CommonInterface,
  CompetencyData,
  CompetencyDataGroup,
  YearData
} from 'core/interface/assessment';
import {
  competencyScoresYearColors,
  generateRandomColor,
  getComparedData
} from 'helpers/getCompared';
import AssessmentYearLabel from '../assessmentYearLabel/Index';
import { useTheme } from '@material-ui/core';
import Cookies from 'js-cookie';
import { LOGOUT_EVENT } from '../../../redux/actions/clearState';

import './Index.scss';
import { Typography } from '@mui/material';

interface CompetencyScoresProps {
  candidateUserId?: string;
}

const CompetencyScores: FC<CompetencyScoresProps> = ({ candidateUserId }) => {
  const { t } = useLocales();
  const theme = useTheme();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAssesmentPopup, setShowAssesmentPopup] = useState<boolean>(false);
  const [competencyYear, setCompetencyYear] = useState<YearData[]>([]);
  const [displayData, setDisplayData] = useState<CompetencyData>();
  const [selectedTab, setSelectedTab] = useState<YearData>();
  const [groups, setGroups] = useState<string[]>([]);
  const [bars, setBars] = useState<CompetencyDataGroup[]>([]);
  const [group, setGroup] = useState<string>('');
  const [currentGroup, setCurrentGroup] = useState<CompetencyData>();
  const [currentCompareGroup, setCurrentCompareGroup] = useState<
    CompetencyData | any
  >();
  const [sorted, setSorted] = useState<CommonInterface[]>();
  const [compareData, setCompareData] = useState<CompetencyData[]>([]);
  const me = useSelector((state: IMe) => state.user).user;
  const userId = candidateUserId || me?.id;

  const { assessmentData } = useSelector(
    (state: RootState) => state.assessment
  );

  const [intervalValue, setIntervalValue] = useState<NodeJS.Timeout | null>(
    null
  );

  const getInitAssessments = async () => {
    if (userId) {
      setIsLoading((prev) => (prev = true));
      await JobService.getassesments(Number(userId));
      setIsLoading((prev) => (prev = false));
    }
  };

  useEffect(() => {
    getInitAssessments();
  }, []);

  const calculateAverages = (data: CompetencyData) => {
    const groupsData =
      data && groups.length
        ? groups.map((group: string) => {
            const current = data[group as keyof typeof data] as any;
            return {
              groupTitle: group,
              list: current?.lables?.map((el: string, i: number) => ({
                label: el,
                description: current.descriptions[i],
                value: current.data[i]
              }))
            };
          })
        : [];
    return groupsData;
  };

  useEffect(() => {
    // Only set up interval if we have a valid user and correct status
    if (userId && selectedTab?.status === 2) {
      const interval = setInterval(async () => {
        try {
          // Check if we still have a valid token before making the request
          const token = Cookies.get('accesstoken');
          if (!token) {
            clearInterval(interval);
            return;
          }

          await JobService.getassesments(Number(userId));
        } catch (err) {
          // If we get an error (like 401), clear the interval
          clearInterval(interval);
          console.error('Error fetching assessments:', err);
        }
      }, 30000);

      setIntervalValue(interval);

      // Cleanup function
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }

    // Clear any existing interval if conditions aren't met
    return () => {
      if (intervalValue) {
        clearInterval(intervalValue);
        setIntervalValue(null);
      }
    };
  }, [selectedTab?.status, userId]); // Only depend on userId

  useEffect(() => {
    const fetchAssessmentResults = async () => {
      try {
        // Check if we have a valid token and user ID
        const token = Cookies.get('accesstoken');
        if (!token || !userId) {
          return;
        }

        await JobService.getAssesmentsResult(Number(userId));
      } catch (err) {
        console.error('Error fetching assessment results:', err);
      }
    };

    fetchAssessmentResults();
  }, [userId]); // Only depend on userId

  useEffect(() => {
    if (!assessmentData || assessmentData.length === 0) return;

    // Filter out items with null reports for competency processing
    const validAssessments = assessmentData.filter(
      (item: any) => item?.report != null
    );

    // Sort all assessments (including those with null reports) for display
    // Sort by created_at date in descending order (newest first)
    const sorted = assessmentData?.sort((a: YearData, b: YearData) => {
      const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    // Sort valid assessments (with reports) for competency processing
    // Sort by created_at date in descending order (newest first)
    const sortedValid = validAssessments?.sort((a: YearData, b: YearData) => {
      const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    setSorted(sorted);
    const dates = sorted.map((el: CommonInterface) => ({
      title: getDateFormatter(el.created_at, 'Y'),
      id: el.id,
      status: el.status,
      created_at: new Date(el.created_at)
    }));

    const isFirstResultOlderThanAYear = dates?.find(
      (date: YearData) =>
        date?.created_at?.getFullYear() == new Date().getFullYear()
    );

    // Find the first valid assessment date to determine which tab should be active
    let firstValidDateId: string | number | undefined;
    if (sortedValid && sortedValid.length > 0) {
      const firstValidDate = dates.find((date: YearData) =>
        sortedValid.some((item: any) => item.id === date.id)
      );
      firstValidDateId = firstValidDate?.id;
    }

    // Check if we need to add current year tab
    const shouldAddCurrentYearTab = !isFirstResultOlderThanAYear;
    let competencyYearData = dates.map((item: YearData, index: number) => {
      const randomColor = generateRandomColor('#1C9DD7', '#a750af');
      // Mark as active if it's the first valid assessment, or if no valid assessments, mark first one
      const isActive = firstValidDateId 
        ? item.id === firstValidDateId 
        : index === 0;

      return {
        ...item,
        isActive,
        color: competencyScoresYearColors[index] ?? randomColor,
        id: String(item.id),
        title: String(item.title)
      };
    });

    // Add current year tab if needed (before setting state)
    if (shouldAddCurrentYearTab) {
      const currentYear = new Date().getFullYear();
      const newTab = {
        title: currentYear.toString(),
        id: `new_tab_${currentYear}`,
        isActive: false,
        color: generateRandomColor('#1C9DD7', '#a750af'),
        created_at: new Date(),
        status: undefined
      };
      competencyYearData = [newTab, ...competencyYearData];
    }

    setCompetencyYear(competencyYearData);

    // Only process competencies from valid assessments (with reports)
    if (sortedValid && sortedValid.length > 0) {
      const result = sortedValid[0]?.report?.competencies;
      const groups = Object.keys(result || []);

      let compareDataArr;
      if (sortedValid.length === 1) {
        // Fallback: all zeros for each group
        const fallback: Record<string, CompetencyData> = {};
        groups.forEach((group: string) => {
          fallback[group] = {
            lables: result[group]?.lables || [],
            descriptions: result[group]?.descriptions || [],
            data: result[group]?.lables?.map(() => 0) || []
          };
        });
        compareDataArr = [fallback];
      } else {
        compareDataArr = sortedValid
          .slice(1)
          .map((item: CommonInterface) => item?.report?.competencies)
          .filter((data: any) => data != null);
      }
      setCompareData(compareDataArr);

      setDisplayData(result);
      setGroups(groups);
      setGroup(groups[0] || '');
      
      // Find the first valid assessment date for selectedTab
      const firstValidDate = dates.find((date: YearData) =>
        sortedValid.some((item: any) => item.id === date.id)
      );

      if (firstValidDate) {
        setSelectedTab({
          isActive: String(firstValidDate.id),
          status: firstValidDate.status,
          date: String(firstValidDate.title)
        });
      } else if (dates[0]) {
        setSelectedTab({
          isActive: String(dates[0].id),
          status: dates[0].status,
          date: String(dates[0].title)
        });
      }
    } else {
      // No valid assessments with reports
      setCompareData([]);
      setDisplayData(undefined);
      setGroups([]);
      setGroup('');
      if (dates[0]) {
        setSelectedTab({
          isActive: String(dates[0].id),
          status: dates[0].status,
          date: String(dates[0].title)
        });
      }
    }
  }, [assessmentData]);

  useEffect(() => {
    if (displayData && group) {
      const current =
        (displayData[group as keyof typeof displayData] as CompetencyData) ||
        null;
      const barData = calculateAverages(displayData);
      setBars(barData);
      setCurrentGroup(current);
    } else if (!displayData) {
      // Clear currentGroup if displayData is not available
      setCurrentGroup(undefined);
    }
    if (compareData && group) {
      const currentCompareGroups = compareData
        .filter((data: any) => data != null)
        .map((data: any) => data?.[group as keyof typeof data] || {});
      const currentCompare =
        currentCompareGroups.length > 0 ? currentCompareGroups : null;
      setCurrentCompareGroup(currentCompare);
    } else {
      setCurrentCompareGroup(undefined);
    }
  }, [displayData, group, compareData, selectedTab]);

  const handleOpenOverview = (): void => {
    setShowPopup(true);
  };
  const handleClose = (): void => {
    setShowPopup(false);
    setShowAssesmentPopup(false);
  };

  const handleOpenAssesment = (): void => {
    setShowAssesmentPopup(true);
  };

  // Helper function to check if data is valid for display
  const hasValidData = (): boolean => {
    if (!selectedTab) return false;
    
    // Check if selected tab has a valid report
    if (sorted) {
      const itemId = typeof selectedTab.isActive === 'string' 
        ? Number(selectedTab.isActive) 
        : selectedTab.isActive;
      const selectedItem = sorted.find(
        (el: CommonInterface) => el.id === itemId
      );
      
      if (!selectedItem?.report) return false;
    }
    
    // Check if we have display data and current group
    if (!displayData || !currentGroup) return false;
    
    // Check if current group has valid data
    if (!currentGroup.lables || !currentGroup.data || currentGroup.lables.length === 0) {
      return false;
    }
    
    return true;
  };

  const handleTabChange = (item: YearData): void => {
    setSelectedTab({
      isActive: String(item?.id),
      status: item?.status,
      date: String(item?.title)
    });

    setCompetencyYear((prevValues) =>
      prevValues.map((prevItem) => ({
        ...prevItem,
        isActive: String(prevItem?.id) === String(item?.id)
      }))
    );

    if (sorted) {
      // Filter to only include items with valid reports
      const validSorted = sorted.filter(
        (el: CommonInterface) => el?.report != null
      );

      // Convert item.id to number for comparison since sorted items have numeric IDs
      const itemId = typeof item?.id === 'string' ? Number(item.id) : item?.id;

      const currentIndex = validSorted.findIndex(
        (el: CommonInterface) => el.id === itemId
      );

      const selectedItem = validSorted.find(
        (el: CommonInterface) => el.id === itemId
      );

      // Only process if the selected item has a valid report
      if (selectedItem?.report) {
        const compData =
          validSorted
            .slice(currentIndex + 1)
            .map((el: CommonInterface) => el?.report?.competencies)
            .filter((data: any) => data != null) || [];

        setCompareData(compData);

        const showData = selectedItem.report?.competencies;

        if (showData) {
          setDisplayData(showData);
          const groups = Object.keys(showData);
          setGroups(groups);
          setGroup(groups[0] || '');
        }
      } else {
        // Selected item has no report, clear the display
        setCompareData([]);
        setDisplayData(undefined);
        setGroups([]);
        setGroup('');
      }
    }
  };
  const comparisonsArray: any[] = [];
  currentCompareGroup?.forEach((element: any) => {
    const comp = getComparedData(currentGroup, element);
    comparisonsArray.push(comp);
  });

  // Add logout event listener
  useEffect(() => {
    const handleLogout = () => {
      if (intervalValue) {
        clearInterval(intervalValue);
        setIntervalValue(null);
      }
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);

    return () => {
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, [intervalValue]);

  return (
    <>
      {!isLoading && (
        <div className="tab__comp__head">
          {competencyYear.map((item, index) => (
            <div
              onClick={() => {
                handleTabChange(item);
              }}
              key={index}
              className={`tab__comp__head__title ${
                item.isActive ? 'active' : ''
              }`}
            >
              <span className="tab__comp__head__title-title">{item.title}</span>
              <span className="tab__comp__head__title-line"></span>
            </div>
          ))}
        </div>
      )}
      <div className="tab__comp__tab">
        {/* It's stuck at status 2 so I had to do this */}
        {selectedTab?.status === 2 || selectedTab?.status === 3 ? (
          <>
            <div className="tab__comp__tab__left">
              <div className="tab__comp__tab__left__options scrollable ">
                <div className="tab__comp__tab__left__options__years">
                  {competencyYear.map((el) => {
                    if (
                      !el.title ||
                      !el.color ||
                      Number(selectedTab.date) < Number(el.title)
                    )
                      return;

                    return (
                      <AssessmentYearLabel
                        year={el.title}
                        color={
                          String(el.id) === String(selectedTab.isActive)
                            ? theme.palette.primary.main
                            : el.color
                        }
                        key={el.id}
                      />
                    );
                  })}
                </div>
                {groups?.map((item, index) => (
                  <div
                    onClick={() => {
                      setGroup(item);
                    }}
                    key={index}
                    className="tab__comp__tab__left__options__option"
                  >
                    <span className="tab__comp__tab__left__options__option-dot"></span>
                    <span
                      className={`tab__comp__tab__left__options__option-title ${
                        item === group ? 'active' : ''
                      }`}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className={'tab__comp__tab__right assesment'}>
              <>
                <ActionButton
                  onClick={handleOpenOverview}
                  iconName="lupa.svg"
                />
                {hasValidData() && group && selectedTab?.date ? (
                  <RadarChartComp
                    boxSx={{ width: '100%' }}
                    labels={currentGroup?.lables}
                    points={currentGroup?.data}
                    descriptions={currentGroup?.descriptions}
                    compareTitle={t('Previous Year Result')}
                    comparePoints={comparisonsArray || ''}
                    assessment={true}
                    chartHeight={400}
                    chartWidth={400}
                    years={competencyYear}
                  />
                ) : selectedTab?.date ? (
                  <div className="tab__comp__tab__right__no-data">
                    <Typography className="tab__comp__tab__right__no-data__text">
                      {t('No competency data available for this year.')}
                    </Typography>
                    <Typography className="tab__comp__tab__right__no-data__text">
                      {t('The assessment may still be in progress or the data is not yet available.')}
                    </Typography>
                    <Typography className="tab__comp__tab__right__no-data__text">
                      {t('Please try again later.')}
                    </Typography>
                  </div>
                ) : null}
              </>
            </div>
          </>
        ) : (
          <div className="assesment_buttons">
            {me?.company ? (
              <span>{t("Candidate didn't take assessment")}</span>
            ) : selectedTab?.status != 2 && selectedTab?.status != 3 ? (
              <Button
                t={t}
                onClick={handleOpenAssesment}
                className="start-assesment"
                title="start_assesment"
              />
            ) : (
              <span>{t('Result will be in shortly')}…</span>
            )}
          </div>
        )}
      </div>
      {showPopup && (
        <Popup
          className="overview-popup"
          title={`Talent overview-${selectedTab?.date}`}
          description="These are your competencies and talents. Please note that a low score does not have to be a
        bad score. It often means that this talent is compensated by other positive talents."
          onClose={handleClose}
        >
          <TalentOverviewPopup bars={bars} />
        </Popup>
      )}
      {showAssesmentPopup && (
        <Popup title={t('Start assessment test')} onClose={handleClose}>
          <AssessmentPopup onBackClick={setShowAssesmentPopup} t={t} />
        </Popup>
      )}
    </>
  );
};

export default CompetencyScores;
